import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch, useSelector } from 'react-redux';
import { useIdle } from 'react-use';

import { createStyles, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { filter, isEmpty, last } from 'lodash';
import { PatientFormTemplate } from 'requests/resources';

import { initApp, signOut } from '@lib/actions';
import { usePrevious } from '@lib/hooks';
import { usersResource } from '@lib/resources';
import { PatientFormTemplateLumaBotFlow } from '@lib/resources/types';
import { selectCurrentUser } from '@lib/selectors';

import { Typography } from 'components/v2';

import { IconPoweredBy } from '../components';
import AvatarLumaBot from '../components/AvatarLumaBot';
import LumaBotLoading from '../components/IconLoading';
import AppBarLumaBot from '../containers/Header/AppBarLumaBot';
import LumaBotMessages from '../containers/Messages/MessagesContainer';
import TextFieldWrapper from '../containers/TextField/TextFieldWrapper';
import { ChatContextProps, ChatProvider } from '../contexts/ChatContext';
import { useLumaBotActions } from '../hooks';

const styles = () =>
  createStyles({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    messages: {
      flex: 1,
    },
    appBar: {
      minHeight: 56,
    },
    poweredByLuma: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },
    iconPoweredBy: {
      fontSize: '14px',
    },
  });

const intlMessages = defineMessages({
  poweredByLuma: {
    id: 'components.v2.LumaBot.poweredByLuma',
    defaultMessage: '{powered} Powered by Luma Health',
  },
});

const oneMinute = 60e3;

export interface LumaBotProps extends WithStyles<typeof styles>, React.HTMLAttributes<HTMLDivElement> {
  flowTemplates?: PatientFormTemplate[];
  flowId?: string;
  variant?: ChatContextProps['variant'];
  minutesKnockKnock?: number;
  minutesSessionTimeout?: number;
  readOnly?: boolean;
  isPopper?: boolean;
  onClose: () => void;
  onMessagesUnread: (count?: number) => void;
  onMessagesRead: () => void;
}

const LumaBot: React.FC<LumaBotProps> = ({
  classes,
  className,
  flowTemplates,
  flowId,
  variant,
  minutesKnockKnock = 10,
  minutesSessionTimeout = 15,
  readOnly,
  isPopper,
  onClose,
  onMessagesUnread,
  onMessagesRead,
  ...rest
}) => {
  const lumaBotContext = useLumaBotActions(flowTemplates, flowId, variant, isPopper);
  const { settings, loading, messages, pushFlowTemplate, pushAnswer, revertMessage, clear } = lumaBotContext;
  const focused = useRef(false);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const widgetUser = useSelector(usersResource.makeSelectEntityFiltered({ $isWidgetUser: true }));
  const currentUser = useSelector(selectCurrentUser)!;

  const isIdle10 = useIdle(minutesKnockKnock * oneMinute);
  const isIdle15 = useIdle(minutesSessionTimeout * oneMinute);

  // knock-knock
  useEffect(() => {
    if (isIdle10 && !readOnly) pushFlowTemplate(PatientFormTemplateLumaBotFlow.KnockKnock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle10, readOnly]);

  // session-timeout
  useEffect(() => {
    if (isIdle15 && !readOnly && !currentUser.$isWidgetUser) {
      dispatch(signOut({}));
      dispatch(
        initApp.request({
          location: { query: { user: widgetUser ? widgetUser.id : 'forceError' } } as any,
          meta: {
            callbackSuccess: () => {
              clear();
              pushFlowTemplate(PatientFormTemplateLumaBotFlow.SessionTimeout);
            },
            // NOTE: This should never happen... reloading just in case
            callbackError: () => window.location.reload(),
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdle15, readOnly]);

  useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        focused.current = false;
      }
    });
  }, []);

  // play notification for new incoming message
  const prevMessages = usePrevious(messages);
  useEffect(() => {
    if (!focused.current) {
      const countMessages = filter(messages, { type: 'inbound' }).length;
      const countPrevMessages = filter(prevMessages, { type: 'inbound' }).length;
      if (countPrevMessages && countPrevMessages < countMessages) {
        onMessagesUnread(countMessages - countPrevMessages);
      }
    }
  }, [messages, prevMessages, onMessagesUnread]);

  if (isEmpty(messages)) {
    return <LumaBotLoading />;
  }

  return (
    <ChatProvider {...lumaBotContext}>
      <AppBarLumaBot onClose={onClose} position="relative" />
      <div
        className={classnames(classes.root, className)}
        onClick={() => {
          focused.current = true;
          onMessagesRead();
        }}
        onFocus={() => {
          focused.current = true;
          onMessagesRead();
        }}
        onBlur={() => {
          focused.current = false;
        }}
        {...rest}
      >
        <LumaBotMessages
          className={classes.messages}
          messages={messages}
          avatarInbound={<AvatarLumaBot size="xxsmall" src={settings.avatar as string} />}
          onSubmit={pushAnswer}
          onRevert={revertMessage}
        />
        <TextFieldWrapper message={last(messages)} disabled={loading} multiline rowsMax={3} onSubmit={pushAnswer} />
        {settings.includePoweredByLuma && (
          <Typography className={classes.poweredByLuma} variant="bW600F12" color="textSecondary">
            {formatMessage(intlMessages.poweredByLuma, {
              powered: <IconPoweredBy className={classes.iconPoweredBy} />,
            })}
          </Typography>
        )}
      </div>
    </ChatProvider>
  );
};

export default withStyles(styles, { name: 'LumaBot' })(LumaBot);
